import { DateRangeCalendar } from "@services/ui-components";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { ReactSelect } from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../../../services/ErrorService";
import { CategoryInterface } from "../../../../services/serviceCategory/ServicesCategoryNewInnit";
import i18next from "i18next";
import i18n from "@me-team/host/src/i18n";
import { CategoryPickerCategory, CategoryPickerComponent } from "@me-pos/category-picker";
import { IOperationsFilter, OptionString } from "../types";
import transformProductCategories from "@me-team/host/src/utils/transformProductCategories";
import { useGetProductCategoriesQuery } from "@me-team/host/main/products/graphql/products.hooks";
import { normalizeCategoryData } from "../../../Products/services/normalizeCategoryData";
import { useGetAllWarehousesQuery, useGetSuppliersQuery } from "@me-team/host/main/warehouses/graphql/warehouses.hooks";
import { useParams } from "react-router-dom";
import { ErrorModalComponent } from "@me-pos/error-modal";
import { initialOperationsFilter } from "../WarehousesOperations";
import { Option } from "@me-team/common/src/components/WarehousesGoods/types";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { StorageProductRelationHistoryAction } from "@me-team/host/main/graphql/types";
import { WarehouseOption } from "../../Reports/types";

interface ProductsFilterWrapperProps {
    setFilters: React.Dispatch<React.SetStateAction<IOperationsFilter>>;
    showFilters: boolean;
}

const initialFiltersRange = {
    startDate: new Date(2020, 0, 1),
    endDate: new Date(),
};

const WarehousesOperationsFilter: React.FC<ProductsFilterWrapperProps> = ({ setFilters, showFilters }) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);

    const operationTypes: Record<StorageProductRelationHistoryAction, string> = {
        [StorageProductRelationHistoryAction.Arrival]: t("Arrival of goods"),
        [StorageProductRelationHistoryAction.WriteOff]: t("Write-off of goods"),
        [StorageProductRelationHistoryAction.Movement]: t("Movement"),
        [StorageProductRelationHistoryAction.Sale]: t("Selling"),
        [StorageProductRelationHistoryAction.BookingWriteOff]: t("Booking write-off of goods"),
    };
    const operationsOptions = Object.entries(operationTypes).map((type) => ({
        value: type[0],
        label: type[1],
    }));
    const statuses = [t("ToolTypeForFeedbackType.Original"), t("ToolTypeForFeedbackType.Edited")];
    const operationsStatusOptions = statuses.map((status, index) => ({
        value: index,
        label: status,
    }));
    const remnants = [`${t("Remnants less than")} 0`, `${t("Remnants")} = 0`, `${t("Remnants greater than")} 0`];
    const remnantOptions = remnants.map((remnant, index) => ({
        value: index - 1,
        label: remnant,
    }));

    const [dateRangeFilter, setDateRangeFilter] = useState(initialFiltersRange);
    const [selectedOperationType, setSelectedOperationType] = useState<OptionString | null>(null);
    const [selectedOperationStatus, setSelectedOperationStatus] = useState<Option | null>(null);
    const [selectedRemnant, setSelectedRemnant] = useState<Option | null>(null);

    const dropdownRef = useRef(null);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [currentNameFilter, setCurrentNameFilter] = useState({
        categoryName: "",
        subCategoryName: "",
    });
    const [currentIdFilter, setCurrentIdFilter] = useState({
        categoryId: null,
        subCategoryId: null,
    });
    const [productName, setProductName] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<CategoryInterface[]>(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState<Option | null>(null);
    const [selectedSupplier, setSelectedSupplier] = useState<Option | null>(null);

    const { data: productCategoriesData } = useGetProductCategoriesQuery({
        variables: {
            itemsPerPage: 1000,
            branchId: Number(branchId),
        },
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems = normalizeCategoryData(data);
            setSelectedCategory(transformProductCategories(normalizedItems));
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsPickerOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const { data: warehousesData } = useGetAllWarehousesQuery({
        variables: {
            currentBranchId: +branchId,
            companyId: +currentCompanyId,
        },
        skip: !currentCompanyId || !branchId,
    });

    const warehousesOptions =
        warehousesData?.user?.company?.branchesPaginated?.branches[0]?.storages.map((storage) => {
            return {
                value: storage.id,
                label: storage.name,
                deleted: storage.deleted,
            };
        }) || [];

    const { data: branchProductSuppliersData, refetch: refetchBranchProductSuppliers } = useGetSuppliersQuery({
        variables: {
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        skip: !currentCompanyId,
        nextFetchPolicy: "network-only",
    });

    useEffect(() => {
        if (showFilters) {
            refetchBranchProductSuppliers();
        }
    }, [showFilters]);

    const productSupplierOptions =
        branchProductSuppliersData?.suppliers.map((supplier) => {
            return {
                value: supplier.id,
                label: supplier.name,
            };
        }) || [];
    const handleCategoryNameChange = (categoryName: string) => {
        setCurrentNameFilter({ categoryName, subCategoryName: "" });
    };
    const handleSubCategoryNameChange = (subCategoryName: string, categoryName: string = "") => {
        setCurrentNameFilter({ categoryName, subCategoryName });
    };
    const handleCategoryIdChange = (categoryId: number) => {
        setCurrentIdFilter({ categoryId, subCategoryId: null });
    };
    const handleSubCategoryIdChange = (categoryId: number, subCategoryId: number) => {
        setCurrentIdFilter((prevState) => ({
            ...prevState,
            categoryId: categoryId,
            subCategoryId: subCategoryId,
        }));
    };

    const handleApplyFilter = () => {
        setFilters({
            ...initialOperationsFilter,
            action: selectedOperationType?.value ?? null,
            edited: selectedOperationStatus ? !!selectedOperationStatus.value : null,
            productName,
            remnants: selectedRemnant ? selectedRemnant.value : null,
            startDate: moment(dateRangeFilter.startDate).format("YYYY-MM-DD"),
            endDate: moment(dateRangeFilter.endDate).format("YYYY-MM-DD"),
            storageId: selectedWarehouse?.value ? selectedWarehouse.value : null,
            productCategory: currentIdFilter?.subCategoryId
                ? currentIdFilter?.subCategoryId
                : currentIdFilter?.categoryId,
            supplier: selectedSupplier?.value ? selectedSupplier?.value : null,
        });
        setIsPickerOpen(false);
    };

    const resetSelectedCategories = (categories: CategoryInterface[]) => {
        return categories?.map((category) => ({
            ...category,
            isPicked: false,
            isSubcategoryPiked: false,
            subcategoryList: category?.subcategoryList?.map((subcategory) => ({
                ...subcategory,
                isPicked: false,
            })),
        }));
    };

    const handleClearFilter = () => {
        setFilters(initialOperationsFilter);
        setCurrentNameFilter({ categoryName: "", subCategoryName: "" });
        setSelectedCategory(null);
        if (selectedCategory) {
            setSelectedCategory(resetSelectedCategories(selectedCategory));
        }
        setIsPickerOpen(false);
        setDateRangeFilter(initialFiltersRange);
        setSelectedOperationType(null);
        setSelectedSupplier(null);
        setSelectedWarehouse(null);
        setProductName("");
        setSelectedOperationStatus(null);
        setSelectedRemnant(null);
        setCurrentIdFilter({ categoryId: null, subCategoryId: null });
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleApplyFilter();
        }
    };

    return (
        <>
            <Row className="g-3">
                <Col lg={3}>
                    <DateRangeCalendar
                        dateRangeFilter={dateRangeFilter}
                        setDateRangeFilter={setDateRangeFilter}
                        disableValidation={true}
                        id="salary-rangeDate-field"
                    />
                </Col>
                <Col lg={3}>
                    <ReactSelect
                        name="operationType"
                        id="productsList-operationType-field"
                        value={selectedOperationType}
                        options={operationsOptions}
                        placeholder={t("Operation type")}
                        onChange={(type: Option | null) => setSelectedOperationType(type)}
                        isSearchable={true}
                        onKeyDown={handleSearchKeyDown}
                    />
                </Col>
                <Col lg={3}>
                    <ReactSelect
                        name="supplier"
                        id="productsList-supplier-field"
                        value={selectedSupplier}
                        options={productSupplierOptions}
                        placeholder={t("Supplier")}
                        onChange={(supplier: Option) => setSelectedSupplier(supplier)}
                        isSearchable={true}
                        onKeyDown={handleSearchKeyDown}
                    />
                </Col>
                <Col lg={3}>
                    <ReactSelect
                        name="warehouseName"
                        id="productsList-warehouseName-field"
                        value={selectedWarehouse}
                        options={warehousesOptions}
                        placeholder={t("Warehouse name")}
                        onChange={(warehouse: Option) => setSelectedWarehouse(warehouse)}
                        isSearchable={true}
                        onKeyDown={handleSearchKeyDown}
                        formatOptionLabel={(option: WarehouseOption) => (
                            <div>
                                <span>{option.label}</span>
                                {option.deleted && <span className="text-grey ms-1">({t("archive")})</span>}
                            </div>
                        )}
                    />
                </Col>
            </Row>
            <Row className="g-3 mt-0">
                <Col lg={10}>
                    <Row className="g-3">
                        <Col lg={3}>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    id="productsList-categoryId-field"
                                    className="form-control p-6 cursor-pointer"
                                    placeholder={t("Category")}
                                    value={
                                        currentNameFilter?.categoryName || currentNameFilter?.subCategoryName
                                            ? `${currentNameFilter?.categoryName}  ${currentNameFilter?.subCategoryName}`
                                            : ""
                                    }
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsPickerOpen(!isPickerOpen);
                                    }}
                                    readOnly
                                />
                                <i className="bi bi-chevron-down position-absolute top-50 end-0 translate-middle-y text-dark me-6"></i>
                                <div
                                    ref={dropdownRef}
                                    className="dropdown position-absolute opacity-1 z-2 top-10  start-0 end-0 mt-2 bg-white shadow"
                                >
                                    {productCategoriesData && selectedCategory && (
                                        <CategoryPickerComponent
                                            i18n={i18next}
                                            isOpen={isPickerOpen}
                                            onSearchChange={() => {}}
                                            onChangeCategory={(currentCategory: CategoryPickerCategory) => {
                                                if (currentCategory.isPicked) {
                                                    handleCategoryNameChange(currentCategory?.name);
                                                    handleCategoryIdChange(currentCategory?.id);
                                                    setIsPickerOpen(false);
                                                } else {
                                                    setCurrentNameFilter({
                                                        categoryName: "",
                                                        subCategoryName: "",
                                                    });
                                                }
                                            }}
                                            onChangeSubcategory={(currentSubcategory, currentCategory) => {
                                                if (currentSubcategory?.isPicked) {
                                                    handleSubCategoryNameChange(currentSubcategory?.name);
                                                    handleSubCategoryIdChange(
                                                        currentCategory?.id,
                                                        currentSubcategory?.id
                                                    );
                                                    setIsPickerOpen(false);
                                                } else {
                                                    setCurrentNameFilter({
                                                        categoryName: "",
                                                        subCategoryName: "",
                                                    });
                                                }
                                            }}
                                            serviceCategories={selectedCategory}
                                        />
                                    )}
                                </div>
                            </div>
                        </Col>
                        <Col lg={3}>
                            <div className="position-relative">
                                <input
                                    name="productName"
                                    id="productsList-productName-field"
                                    type="text"
                                    className="form-control px-6 pe-4"
                                    placeholder={t("Product Name")}
                                    value={productName}
                                    onChange={(e) => setProductName(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                />
                                <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-2"></i>
                            </div>
                        </Col>
                        <Col lg={3}>
                            <ReactSelect
                                name="operationsStatus"
                                id="productsList-operationsStatus-field"
                                value={selectedOperationStatus}
                                options={operationsStatusOptions}
                                placeholder={t("Operations status")}
                                onChange={(status: Option | null) => setSelectedOperationStatus(status)}
                                isSearchable={true}
                                onKeyDown={handleSearchKeyDown}
                            />
                        </Col>
                        <Col lg={3}>
                            <ReactSelect
                                name="remnant"
                                id="productsList-remnant-field"
                                value={selectedRemnant}
                                options={remnantOptions}
                                placeholder={t("Remnants after performance")}
                                onChange={(remnant: Option | null) => setSelectedRemnant(remnant)}
                                isSearchable={true}
                                onKeyDown={handleSearchKeyDown}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col lg={2}>
                    <Row className="g-3 h-100">
                        <Col xs={6}>
                            <Button
                                type="button"
                                variant="outline-dark"
                                className="border-grey w-100 h-100 px-1 fw-normal text-truncate"
                                onClick={handleClearFilter}
                            >
                                {t("clear")}
                            </Button>
                        </Col>
                        <Col xs={6}>
                            <Button
                                variant="primary"
                                className="w-100 h-100 px-1 fw-normal text-truncate"
                                onClick={handleApplyFilter}
                            >
                                {t("apply")}
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default WarehousesOperationsFilter;
